import {
  eanValidator,
  globalMaterialNameValidator,
  measurementUnitValidator,
  customMeasurementUnitValidator,
  deliveryPlaceValidator,
  detailsValidator,
  newMaterialGoodsValidationSchema,
  NewMaterialFormValues,
} from "../NewMaterialForm/newMaterialFormValidationSchema";
import {
  capitalize,
  CERTIFICATIONS,
  MEASUREMENT_UNIT_LIST,
  TO_BE_AGREED,
} from "../../utils";
import i18next from "../../i18n";
import {
  CertificationRequest,
  MaterialCertificationRef,
  MaterialResponse,
  MaterialType,
} from "../../types";

/**
 * The edit-material form (`PUT /material/{id}`, Task 7) collects the exact same field set as the
 * create form's Goods kind (Task 6) — there is no Services/Goods toggle on edit, and the
 * pre-Carbon `EditMaterialForm.tsx` never rendered one either (`material.type` was never read by
 * that component at all). Reusing the TYPE, not re-declaring an equivalent one, is what makes
 * `MaterialGoodsFields`/`MaterialCertifications`/`MaterialFileUpload` (Task 6's own sub-components)
 * directly reusable here with no adapter layer: they're typed against `NewMaterialFormValues`
 * already, so a structurally-identical `EditMaterialFormValues` would only add friction.
 */
export type EditMaterialFormValues = NewMaterialFormValues;

/**
 * `deliveryPlace`: the one field where reusing `newMaterialGoodsValidationSchema` verbatim is a
 * deliberate STRENGTHENING, not a silent regression — see `task-7-report.md`'s OLD→NEW table for
 * the full writeup. Short version: the pre-Carbon edit form's own Yup schema
 * (`utils/validations.ts#editMaterialValidationSchema`, deleted by this task — grepped for other
 * importers first, found none) never actually required `deliveryPlace` (`Yup.string()`, no
 * `.required()`), but its `<select>` always carried a real, non-empty value anyway — either a
 * site's id or the literal "to be agreed" sentinel string rendered as that option's OWN visible
 * text (not a blank placeholder). `deliveryPlaceValidator`'s `.required()` can only ever reject a
 * genuinely EMPTY string, which this field's own option list (see `MaterialGoodsFields.tsx`,
 * reused verbatim here) never produces — `TO_BE_AGREED` is a real, always-present, non-empty
 * option, and `buildEditMaterialFormValues` below always seeds a defined value. So the stricter
 * schema changes nothing an old, untouched submission could actually hit.
 *
 * Every other field here is either unchanged from the old schema (`ean`, `globalMaterialName`,
 * `measurementUnit`'s requiredness, `customMeasurementUnit`'s conditional requirement,
 * `deliveryFormat`/`brand`/`comments` staying unvalidated) or a field the old edit form never
 * rendered at all (`details` — the pre-Carbon JSX had no control for it, so there was no rule to
 * carry forward; Task 6's report flagged `details` being dropped from the CREATE payload, and this
 * task's brief extends the same fix to the edit form: render it, preload it, validate it,
 * `.max(255)` included, and send it).
 */
export const editMaterialValidationSchema = newMaterialGoodsValidationSchema;

// Re-exported so a caller of this module never has to reach into `NewMaterialForm/` directly for
// the field-level rules this schema is built from — same discoverability `newMaterialFormValidationSchema.ts`'s
// own docstring anticipates ("a later form on overlapping fields ... can compose its own schema
// out of these").
export {
  eanValidator,
  globalMaterialNameValidator,
  measurementUnitValidator,
  customMeasurementUnitValidator,
  deliveryPlaceValidator,
  detailsValidator,
};

const isKnownMeasurementUnit = (unit: string) =>
  (MEASUREMENT_UNIT_LIST ?? []).some((entry) => entry.name === unit);

/**
 * Builds the RHF `defaultValues` for every field `MaterialGoodsFields`/the ean+name pair actually
 * render — the PRELOAD table this task's brief demands, made into one pure, unit-testable
 * function instead of inline object literal JSX can't be tested without rendering (same principle
 * `itemStatus.ts`/`buildEditOrderEntry.ts` already establish in this codebase — see the FE
 * CLAUDE.md's "pure helpers extracted out of the big containers" convention).
 *
 * `measurementUnit`/`customMeasurementUnit`: the pre-Carbon form's OWN preload for
 * `customMeasurementUnit` was dead code — it only populated when
 * `material.measurementUnit === i18next.t("measurementUnits.other")`, but `onSubmit` NEVER
 * persists the literal "Other" sentinel (it substitutes the typed custom text before sending — see
 * this same file's history), so that condition could never be true for a real saved material and
 * `customMeasurementUnit` always preloaded blank. This never lost data on an untouched save (the
 * raw `measurementUnit` value round-tripped through unchanged either way), but it DID mean a
 * material saved with a custom unit showed an unselected dropdown with no visible custom value —
 * a real gap, closed here: if the material's stored unit isn't one of `MEASUREMENT_UNIT_LIST`'s
 * known names, this treats it as a custom entry, seeding the select with the "Other" sentinel and
 * `customMeasurementUnit` with the material's actual text, so the field the user actually typed
 * is visible again instead of silently hidden behind an unselected dropdown.
 */
export const buildEditMaterialFormValues = (
  material: MaterialResponse
): EditMaterialFormValues => {
  const storedUnit = material?.measurementUnit ?? "";
  const isCustomUnit = storedUnit !== "" && !isKnownMeasurementUnit(storedUnit);

  return {
    ean: material?.ean ?? "",
    globalMaterialName: material?.globalMaterialName ?? "",
    measurementUnit: isCustomUnit
      ? i18next.t("measurementUnits.other")
      : storedUnit,
    customMeasurementUnit: isCustomUnit ? storedUnit : "",
    deliveryFormat: material?.deliveryFormat ?? "",
    deliveryPlace: material?.deliveryPlace?.id ?? TO_BE_AGREED,
    brand: material?.brand ?? "",
    details: material?.details ?? "",
    comments: material?.comments ?? "",
  };
};

/**
 * `material.certifications` arrives as the lean `MaterialCertificationRef[]` (id/name/required/
 * type, no `label`) — `MaterialCertifications.tsx` (reused verbatim from Task 6) manages a
 * `CertificationRequest[]` (has `label`, no `id`), the exact shape `POST /material`'s own
 * `certifications` key expects and what the "add" flow inside that component builds by hand from
 * `CERTIFICATIONS[type].label`. This reconstructs the same `label` the same way — from `type`,
 * via the identical lookup table — so a certification loaded from the record renders and re-saves
 * identically to one added fresh in this session. A `type` with no entry in `CERTIFICATIONS`
 * (shouldn't happen — the add flow can only pick a listed type — but the response is server data,
 * not something this form controls) falls back to an empty label rather than throwing.
 */
export const mapCertificationResponsesToRequests = (
  certifications: MaterialCertificationRef[] | undefined
): CertificationRequest[] =>
  (certifications ?? []).map((certification) => ({
    name: certification.name,
    type: certification.type,
    required: certification.required,
    label: CERTIFICATIONS[certification.type]?.label ?? "",
  }));

/**
 * A material has no type of its own: what makes it a service is its category, the same field the
 * materials list filters on.
 */
export const isServiceMaterial = (material?: MaterialResponse) =>
  material?.category?.type === MaterialType.SERVICES;

/**
 * The `PUT /material/{id}` body for a service (SCRUM-32). The form only shows its code and name,
 * but the backend overwrites every field with what it receives, so the rest goes back exactly as
 * stored: an edit must not wipe what a service happened to have from before.
 */
export const buildServiceEditBody = (
  material: MaterialResponse,
  values: Pick<EditMaterialFormValues, "ean" | "globalMaterialName">
) => ({
  ean: values.ean,
  globalMaterialName: capitalize(values.globalMaterialName) || "",
  deliveryFormat: material.deliveryFormat ?? "",
  deliveryPlace: material.deliveryPlace?.id
    ? { id: material.deliveryPlace.id }
    : null,
  measurementUnit: material.measurementUnit ?? "",
  comments: material.comments ?? "",
  brand: material.brand ?? "",
  details: material.details ?? "",
  confidentSpec: material.confidentSpec ?? false,
  confident: material.confident ?? false,
  fileURL: material.fileURL ?? "",
  certifications: mapCertificationResponsesToRequests(material.certifications),
  tags: (material.tags ?? []).map((tag) => ({ id: tag.id })),
});
